import { getTheme, ITheme } from '@fluentui/react';
import { AttachmentDto } from 'services/client-api-wrapper/campus_api/responses.dto';
import { hardcodedAddOns } from 'features/Rooms/RoomView/addons';
import IvicosStrings from 'kits/language/stringKit';
import { useCallback, useEffect, useState } from 'react';
import { SetterOrUpdater, useRecoilState } from 'recoil';
import { resourceService } from 'services/api/resource.service';
import { useShowAttachmentPanel } from 'shared-state/display-options/hooks';
import { useLocalProfile } from 'shared-state/identity/hooks';
import { IProfile } from 'shared-state/identity/types';
import { useCurrentAreaId, useCurrentCampusId, useCurrentRoomId, useSelectedAttachment } from 'shared-state/location/hook';
import { ParticipantInfo, useYoutubeVideoAttachmentManager } from '../YoutubeVideoAttachmentManager';
import { useEvent } from 'react-use';
import { useEmittingEvents } from 'services/socket-connection/socket-adapters/event-actions/emittingEvents';
import { aIsAttachmentCreationModalOpen } from 'features/LinkAttachmentModalBox/atom';
import { useUsersInRoom } from 'shared-state/presence/hooks';
import { getAuthHeader } from 'services/client-api-wrapper/services/auth_header';
import { getApiGatewayUrl } from 'kits/urlKit';

export type PanelType = 'closed' | 'link' | 'tool';
export interface IAttachmentsPanelState {
    campusId?: string;
    areaId?: string;
    roomId?: string;
    showAttachmentPanel: PanelType;
    theme: ITheme;
    setShowAttachmentPanel: SetterOrUpdater<PanelType>;
    isAttachmentCreationModalOpen: boolean;
    setIsAttachmentCreationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedAttachment: SetterOrUpdater<
        | {
              addOnId: string;
              ref: string;
              description: string;
          }
        | undefined
    >;
    refetchAttachments: () => Promise<void>;
    refetchAttachmentsOnCreation: () => Promise<void>;
    isAttachmentUpdateModalOpen: boolean;
    attachmentToEdit?: AttachmentDto;
    setIsAttachmentUpdateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setAttachmentToEdit: React.Dispatch<React.SetStateAction<AttachmentDto | undefined>>;
    filter?: string;
    setFilter: React.Dispatch<React.SetStateAction<string | undefined>>;
    attachments: AttachmentDto[];

    addOn: (addOnName: string) =>
        | {
              id: string;
              type: string;
              attributes: {
                  urlTemplate: string;
                  iconUrl: string;
                  displayName: string;
                  description: string;
              };
              relationships: {};
          }
        | undefined;
    localProfile?: IProfile;

    onDelete: (attachmentId: any) => Promise<void>;
    onDragEnd: (result: any) => Promise<void>;
    filterAttachment: (filter: any) => void;
    activeAttachmentId: string;
    setActiveAttachmentId: React.Dispatch<React.SetStateAction<string>>;
    updateAttachmentPinStatus: (item: AttachmentDto, checked: boolean) => Promise<void>;
    videoThumbnailClick: (itemId: AttachmentDto) => void;
    calculateActualVideoStatus: (item: AttachmentDto) => 'playing' | 'stopped' | 'controlledByRemoteUser';
    videoWatchersList: ParticipantInfo[];
    isLocalUserVideoOwner: boolean;
    videoOwnerParticipant: undefined | ParticipantInfo;
    changeVideoOwner: (newVideoOwnerId: string) => void;
    jitsiApi: any;
    isVideoOwnershipTransferedToLocal: boolean;
    previousVideoOwnerParticipant: undefined | ParticipantInfo;
}

export const useAttachmentsPanelState: (jitsiApi: any) => IAttachmentsPanelState = (jitsiApi) => {
    const [attachments, setAttachments] = useState<AttachmentDto[]>([]);
    const [campusId] = useCurrentCampusId();
    const [areaId] = useCurrentAreaId();
    const [roomId] = useCurrentRoomId();
    const localProfile = useLocalProfile();

    const [showAttachmentPanel, setShowAttachmentPanel] = useShowAttachmentPanel();
    const [filter, setFilter] = useState<string | undefined>();
    const addOns = hardcodedAddOns;
    const [, setSelectedAttachment] = useSelectedAttachment();
    const [attachmentToEdit, setAttachmentToEdit] = useState<AttachmentDto | undefined>();
    const [isAttachmentCreationModalOpen, setIsAttachmentCreationModalOpen] = useRecoilState(aIsAttachmentCreationModalOpen);
    const [isAttachmentUpdateModalOpen, setIsAttachmentUpdateModalOpen] = useState<boolean>(false);
    const [activeAttachmentId, setActiveAttachmentId] = useState('');
    const { sendAttachmentsUpdatedEvent } = useEmittingEvents();
    const {
        playVideo,
        stopVideo,
        changeVideoOwner,
        playingVideoId,
        playingVideoOwnerId,
        isLocalUserVideoOwner,
        videoWatchersList,
        isVideoOwnershipTransferedToLocal,
        previousVideoOwnerId
    } = useYoutubeVideoAttachmentManager(jitsiApi);

    const calculateActualVideoStatus = (item: AttachmentDto) => {
        const playingStatus: 'playing' | 'stopped' | 'controlledByRemoteUser' = item.ref === playingVideoId ? 'playing' : 'stopped';

        return playingStatus;
    };

    const updateAttachmentPinStatus = async (item: AttachmentDto) => {
        if (!campusId || !areaId || !roomId || !item.id) return;
        await resourceService.updateAttachment(campusId, areaId, roomId, item.id, {});
        setIsAttachmentUpdateModalOpen(false);
        sendAttachmentsUpdatedEvent(roomId);
        refetchAttachments();
        setAttachmentToEdit(undefined);
    };

    useEffect(() => {
        showAttachmentPanel !== 'closed' && refetchAttachments();
    }, [campusId, roomId, areaId, showAttachmentPanel]);

    useEffect(() => {
        setSelectedAttachment(undefined);
    }, [campusId, roomId, areaId]);

    const refetchAttachments = async () => {
        if (!campusId || !areaId || !roomId) return;

        const newAttachments = await resourceService.getAllForRoom(campusId, areaId, roomId);

        const sortedAttachments = newAttachments.sort((a, b) => Number(a.position) - Number(b.position));
        setAttachments(sortedAttachments);

        reorderAttachments(sortedAttachments, false);
    };

    useEvent('room-attachments-updated', () => {
        refetchAttachments();
    });

    const refetchAttachmentsOnCreation = async () => {
        if (!campusId || !areaId || !roomId) return;

        const newAttachments = await resourceService.getAllForRoom(campusId, areaId, roomId);

        newAttachments.sort((a, b) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf());
        //  Adjust the position of the rest of the attachments
        for (let i = 1; i < newAttachments.length; i++) {
            newAttachments[i].position = '' + i;
        }
        sendAttachmentsUpdatedEvent(roomId);
        setAttachments(newAttachments);
        reorderAttachments(newAttachments, true);
    };

    const filterAttachment = async (filterValue: any) => {
        const allAttachments = await resourceService.getAllForRoom(campusId, areaId, roomId);
        const filteredAttachments = allAttachments.filter((item) => {
            if (!filterValue && filterValue === '') return allAttachments;

            if (addOn(item.addOnName)?.attributes.displayName.includes(filterValue)) return true;
            return false;
        });
        const sortedAttachments = [...filteredAttachments].sort((a, b) => Number(a.position) - Number(b.position));
        setAttachments(sortedAttachments);
    };

    const theme = getTheme();
    const addOn = (addOnName: string) => addOns.find((addon) => addon.id == addOnName);

    const usersInRoom: IProfile[] = useUsersInRoom(roomId);

    const [lastAttachmentUpdated, setLastAttachmentUpdated] = useState<number>(new Date().getTime());

    // TODO: Not active yet, first CORS endpoint should be able to verify the collaboard link

    const checkConceptBoard = useCallback(
        async (boardUrl: string): Promise<boolean> => {
            try {
                // const corsProxyAddress = process.env.REACT_APP_CORS_PROXY_ADDRESS;
                const corsProxyAddress = getApiGatewayUrl('cors', 'default');

                const encodedBoardUrl = encodeURIComponent(boardUrl);
                const response = await fetch(`${corsProxyAddress}/checkConceptBoard?board=${encodedBoardUrl}&roomId=${roomId}`, {
                    method: 'GET',
                    headers: getAuthHeader()
                });
                const jsonResponse = await response.json();
                return jsonResponse.isValid || jsonResponse.isValid === undefined;
            } catch (error) {
                console.error('Error checking board:', error);
                return false;
            }
        },
        [roomId]
    );

    const removeInvalidConceptboardLinks = async () => {
        console.log('Removing Invalid Conceptboard Links');

        const sortedNonVisitorTypes = usersInRoom
            .filter((user) => ['user', 'manager', 'owner'].includes(user.type))
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
        const isLocalUserResponsibleForDeletion = sortedNonVisitorTypes.length > 0 && sortedNonVisitorTypes[0].uid === localProfile?.uid;

        if (!campusId || !areaId || !roomId || !isLocalUserResponsibleForDeletion) return;

        const temporaryConceptboardAttachments = attachments.filter((att) => att.addOnName === 'conceptboardlinkmanager' && !att.isPinned);
        const invalidAttachments: AttachmentDto[] = [];

        for (const attachment of temporaryConceptboardAttachments) {
            const isValid = await checkConceptBoard(attachment.ref);
            if (!isValid) {
                invalidAttachments.push(attachment);
            }
        }

        for (const attachment of invalidAttachments) {
            try {
                await resourceService.deleteAttachment(campusId, areaId, roomId, attachment.id);
            } catch (error) {
                console.error('Failed to delete invalid attachment:', error);
                return;
            }
        }

        if (invalidAttachments.length > 0) {
            await refetchAttachments();
            sendAttachmentsUpdatedEvent(roomId);
        }
        setLastAttachmentUpdated(new Date().getTime());
    };

    useEffect(() => {
        const currentTime = new Date().getTime();
        attachments.length > 0 && currentTime - 1000 > lastAttachmentUpdated && removeInvalidConceptboardLinks();
    }, [attachments]);

    const onDelete = async (attachmentId: any) => {
        try {
            if (confirm(IvicosStrings.deleteAttachmentConfirmationTitle)) {
                await resourceService.deleteAttachment(campusId, areaId, roomId, attachmentId);
                refetchAttachments();
                sendAttachmentsUpdatedEvent(roomId);
            }
        } catch (e) {
            console.log('Error');
        }
    };

    const reorderAttachments = async (sortedAttachments: AttachmentDto[], reorderFetch: boolean) => {
        const newOrder = sortedAttachments.map((attachment, index) => ({
            attachmentId: attachment.id,
            position: index.toString()
        }));

        try {
            await resourceService.reorderAttachments(campusId, areaId, roomId, newOrder);
            reorderFetch && sendAttachmentsUpdatedEvent(roomId);
        } catch (error) {
            console.log('error', error);
        }
    };

    const onDragEnd = async (result: any) => {
        const { destination, source } = result;

        if (!destination || destination === null || destination.droppableId === null) {
            return;
        }

        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }
        const newPosition = destination.index;
        const items = attachments;
        const updatedArray = items.filter((item: any, index: number) => index != source.index);
        const [draggedItem] = items.filter((item: any, index: number) => index === source.index);
        updatedArray.splice(newPosition, 0, draggedItem);
        setAttachments(updatedArray);
        reorderAttachments(updatedArray, false);
    };
    const videoThumbnailClick = (item: AttachmentDto) => {
        if (isLocalUserVideoOwner) calculateActualVideoStatus(item) === 'stopped' ? playVideo(item.ref) : stopVideo();
        else calculateActualVideoStatus(item) === 'stopped' && playVideo(item.ref);
    };

    return {
        campusId,
        areaId,
        roomId,
        showAttachmentPanel,
        theme,
        setShowAttachmentPanel,
        isAttachmentCreationModalOpen,
        setIsAttachmentCreationModalOpen,
        setSelectedAttachment,
        refetchAttachments,
        refetchAttachmentsOnCreation,
        isAttachmentUpdateModalOpen,
        attachmentToEdit,
        setIsAttachmentUpdateModalOpen,
        setAttachmentToEdit,
        filter,
        setFilter,
        attachments: attachments.filter((att) => !(att.addOnName === 'collaboardlinkmanager' || att.addOnName === 'tldraw')),
        addOn,
        localProfile,
        onDelete,
        onDragEnd,
        filterAttachment,
        activeAttachmentId,
        setActiveAttachmentId,
        updateAttachmentPinStatus,
        videoThumbnailClick,
        calculateActualVideoStatus,
        isLocalUserVideoOwner,
        videoWatchersList,
        videoOwnerParticipant: videoWatchersList.find((p) => p.participantId === playingVideoOwnerId),
        changeVideoOwner,
        jitsiApi,
        isVideoOwnershipTransferedToLocal,
        previousVideoOwnerParticipant: videoWatchersList.find((p) => p.participantId === previousVideoOwnerId)
    };
};
